import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiDollarSign, FiPlus, FiEdit2, FiTrash2, FiArrowLeft } from "react-icons/fi";
import {
    getHostels,
    getHostelFeeStructures,
    deleteHostelFeeStructure,
} from "../../services/hostelService";
import { academicYearService } from "../../services/yearService";

export default function HostelFeeStructureList() {
    const navigate = useNavigate();

    const [academicYears, setAcademicYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState("");
    const [hostels, setHostels] = useState([]);
    const [selectedHostel, setSelectedHostel] = useState("");
    const [structures, setStructures] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([academicYearService.getAll(), getHostels()])
            .then(([ayRes, hRes]) => {
                const years = ayRes.data || [];
                setAcademicYears(years);
                setHostels(hRes.data || []);
                const active = years.find((y) => y.isActive) || years[0];
                if (active) setSelectedYear(active._id);
            })
            .catch(() => toast.error("Failed to load hostels/academic years"));
    }, []);

    const loadStructures = async () => {
        if (!selectedYear) return;
        setLoading(true);
        try {
            const params = { academicYear: selectedYear };
            if (selectedHostel) params.hostel = selectedHostel;
            const res = await getHostelFeeStructures(params);
            setStructures(res.data || []);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to load fee structures");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStructures();
    }, [selectedYear, selectedHostel]); // eslint-disable-line

    const handleDelete = async (structure) => {
        if (
            !window.confirm(
                `Delete the fee structure for ${structure.hostel?.name} - ${structure.roomType}?`,
            )
        )
            return;
        try {
            await deleteHostelFeeStructure(structure._id);
            toast.success("Fee structure deleted");
            loadStructures();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to delete");
        }
    };

    const getTotal = (structure) =>
        structure.heads.reduce((sum, h) => sum + h.amount, 0);

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <FiDollarSign /> Hostel Fee Structures
                    </h1>
                    <p className="page-subtitle">{structures.length} structure(s)</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/hostels")}>
                        <FiArrowLeft /> Back
                    </button>
                    <Link to="/hostel-fee-structures/new" className="btn btn-primary">
                        <FiPlus /> Add Structure
                    </Link>
                </div>
            </div>

            <div className="card">
                <div className="filter-bar">
                    <label>Academic Year:</label>
                    <select
                        className="form-control"
                        style={{ width: 160 }}
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                    >
                        {academicYears.map((y) => (
                            <option key={y._id} value={y._id}>
                                {y.label}
                            </option>
                        ))}
                    </select>

                    <label>Hostel:</label>
                    <select
                        className="form-control"
                        style={{ width: 180 }}
                        value={selectedHostel}
                        onChange={(e) => setSelectedHostel(e.target.value)}
                    >
                        <option value="">All Hostels</option>
                        {hostels.map((h) => (
                            <option key={h._id} value={h._id}>
                                {h.name}
                            </option>
                        ))}
                    </select>
                </div>

                {loading ? (
                    <div className="loading-center">
                        <div className="spinner" />
                        Loading fee structures...
                    </div>
                ) : structures.length === 0 ? (
                    <div className="empty-state">
                        <p>No fee structures defined for this selection yet.</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Hostel</th>
                                    <th>Room Type</th>
                                    <th>Heads</th>
                                    <th>Total</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {structures.map((s) => (
                                    <tr key={s._id}>
                                        <td>{s.hostel?.name}</td>
                                        <td>{s.roomType}</td>
                                        <td>
                                            {s.heads.map((h) => (
                                                <div key={h._id} style={{ fontSize: 12 }}>
                                                    {h.name}: ₹{h.amount.toLocaleString("en-IN")}
                                                </div>
                                            ))}
                                        </td>
                                        <td style={{ fontWeight: 600 }}>₹{getTotal(s).toLocaleString("en-IN")}</td>
                                        <td>
                                            <span className={`badge ${s.isActive ? "badge-available" : "badge-muted"}`}>
                                                {s.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="table-actions" style={{ justifyContent: "flex-end" }}>
                                                <button
                                                    className="btn btn-icon"
                                                    onClick={() => navigate(`/hostel-fee-structures/${s._id}/edit`)}
                                                    title="Edit"
                                                >
                                                    <FiEdit2 />
                                                </button>
                                                <button
                                                    className="btn btn-icon"
                                                    onClick={() => handleDelete(s)}
                                                    title="Delete"
                                                    style={{ color: "var(--danger)" }}
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}